import PropTypes from 'prop-types'
import React, { PureComponent } from 'react'

import {
  StyledBlinkCursor,
  StyledCommand,
  StyledHeader,
  StyledHeaderDotItem,
  StyledHeaderDotList,
  StyledHeaderTitle,
  StyledInput,
  StyledInputWrapper,
  StyledLine,
  StyledLoadingCursor,
  StyledPrompt,
  StyledTerminal,
  StyledTerminalInner,
  StyledTerminalWrapper,
} from './style'

import { CSSTransition, TransitionGroup } from 'react-transition-group'

import { systemCmdList, tipCmdList } from './commands'

import { setCaretPosition } from './utils'

const getAliasMap = (commandList) =>
  Object.entries(commandList).reduce((aliasMap, [key, command]) => {
    const aliases = command.aliasList || []
    aliases.forEach((alias) => {
      aliasMap[alias] = key
    })

    return aliasMap
  }, {})

const resolveCommandEntry = (commandList, aliasMap, input) => {
  if (commandList[input]) {
    return { key: input, command: commandList[input] }
  }

  const canonicalKey = aliasMap[input]
  if (!canonicalKey) {
    return null
  }

  return { key: canonicalKey, command: commandList[canonicalKey] }
}

const resolveSystemCommandEntry = (input) => {
  const entry = Object.entries(systemCmdList).find(([, command]) =>
    command.aliasList.includes(input)
  )

  if (!entry) {
    return null
  }

  const [key, command] = entry
  return { key, command }
}

const formatCommandDescription = (command) => {
  const description = command.content || command.description || 'No description available.'
  const aliasText = command.aliasList && command.aliasList.length
    ? ` Aliases: ${command.aliasList.join(', ')}.`
    : ''

  return `${description}${aliasText}`
}

const getEditDistance = (source, target) => {
  const sourceLength = source.length
  const targetLength = target.length
  const matrix = Array.from({ length: sourceLength + 1 }, () =>
    Array(targetLength + 1).fill(0)
  )

  for (let i = 0; i <= sourceLength; i += 1) {
    matrix[i][0] = i
  }

  for (let j = 0; j <= targetLength; j += 1) {
    matrix[0][j] = j
  }

  for (let i = 1; i <= sourceLength; i += 1) {
    for (let j = 1; j <= targetLength; j += 1) {
      const cost = source[i - 1] === target[j - 1] ? 0 : 1
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      )
    }
  }

  return matrix[sourceLength][targetLength]
}

class Terminal extends PureComponent {
  static propTypes = {
    cmd: PropTypes.shape({
      dynamicList: PropTypes.object,
      staticList: PropTypes.object,
    }).isRequired,
    config: PropTypes.shape({
      initialDirectory: PropTypes.string,
      prompt: PropTypes.string,
      version: PropTypes.string,
      bootCmd: PropTypes.string,
      welcomeMessage: PropTypes.string,
      featuredCommands: PropTypes.arrayOf(PropTypes.string),
    }),
    className: PropTypes.string,
  };

  static defaultProps = {
    className: 'react-terimnal-app',
    config: {
      initialDirectory: 'src',
      prompt: '➜  ~ ',
      version: '1.0.0',
      bootCmd: 'intro',
    },
  };

  historyCmdList = [];
  historyCmdIndex = 0;

  constructor(props) {
    super(props)
    this.$terminal = React.createRef()
    this.$inputWrapper = React.createRef()
    this.$inputEl = React.createRef()

    const { config, cmd } = props

    this.staticCommandAliasMap = getAliasMap(cmd.staticList)
    this.dynamicCommandAliasMap = getAliasMap(cmd.dynamicList)
    this.state = {
      cmdList: [],
      command: '',
      directory: config.initialDirectory,
      currentPrompt: config.prompt || `➜  ${config.initialDirectory} `,
      isPrinting: true,
      cursorPosition: 0,
      processes: [
        { pid: 1, name: 'system', status: 'running' },
        { pid: 2, name: 'terminal', status: 'running' },
      ],
    }

    this.supportedCmdList = [
      ...Object.keys(cmd.staticList),
      ...Object.keys(cmd.dynamicList),
    ]
    this.allCmdList = Array.from(new Set([
      ...this.supportedCmdList,
      ...Object.values(cmd.staticList)
        .map((command) => command.aliasList || [])
        .flat(1),
      ...Object.values(cmd.dynamicList)
        .map((command) => command.aliasList || [])
        .flat(1),
      ...Object.keys(systemCmdList)
        .map((key) => systemCmdList[key].aliasList)
        .flat(1),
    ]))
  }

  componentDidMount() {
    const {
      config: { bootCmd, welcomeMessage },
    } = this.props

    // Add immediate focus
    setTimeout(() => {
      this.inputFocus()
    }, 100)

    if (welcomeMessage) {
      this.print({
        type: 'system',
        label: 'Welcome',
        content: welcomeMessage,
      })
    }

    this.run(bootCmd).then(() => {
      const featuredCommands = this.getFeaturedCommandList()

      if (featuredCommands.length) {
        this.print([
          {
            type: 'system',
            label: 'Start',
            content: 'Recommended commands for first-time visitors.',
          },
          ...featuredCommands,
          {
            type: 'system',
            label: 'Help',
            content: 'Type `help` to see the full command list.',
          },
        ])
      } else {
        const { help, clear, exit } = systemCmdList
        this.print([help, clear, exit])
      }

      this.inputFocus()
    })
  }

  run = (command, inputCommand = this.state.command) => {
    const { cmd } = this.props
    this.setState({ isPrinting: true })
    return cmd.dynamicList[command]
      .run(this.print, inputCommand)
      .then(this.print)
      .catch((error) => {
        console.error(error)
        this.print(tipCmdList.error)
      })
      .finally(() => {
        this.setState({ isPrinting: false })
      })
  };

  print = (cmd) => {
    this.setState((prevState) => ({
      cmdList: [...prevState.cmdList, ...(Array.isArray(cmd) ? cmd : [cmd])],
    }))
    this.autoScroll()
  };

  inputFocus = () => {
    if (this.$inputEl.current) {
      this.$inputEl.current.focus()
    }
  };

  autoScroll = () => {
    this.$terminal.current.scrollTop = this.$inputWrapper.current.offsetTop
  };

  handleKeyCommand = (e) => {
    const {
      config: { prompt },
    } = this.props
    const isDownKey = e.keyCode === 40
    const isUpKey = e.keyCode === 38
    const isTabKey = e.keyCode === 9
    const isCKey = e.keyCode === 67
    const isDKey = e.keyCode === 68
    const isLKey = e.keyCode === 76
    const isLeftKey = e.keyCode === 37
    const isRightKey = e.keyCode === 39
    const isCtrlCKey = isCKey && e.ctrlKey && !e.shiftKey
    const isCtrlDKey = isDKey && e.ctrlKey && !e.shiftKey
    const isCtrlLKey = isLKey && e.ctrlKey && !e.shiftKey

    const { command, isPrinting } = this.state
    if (isPrinting) {
      return
    }

    if (isLeftKey || isRightKey) {
      const newPos = isLeftKey
        ? Math.max(0, this.state.cursorPosition - 1)
        : Math.min(this.state.command.length, this.state.cursorPosition + 1)
      this.setState({ cursorPosition: newPos }, () => {
        setCaretPosition(this.$inputEl.current, newPos)
      })
      e.preventDefault()
    }

    if (isDownKey) {
      this.historyCmdIndex = Math.min(
        this.historyCmdIndex + 1,
        this.historyCmdList.length - 1
      )
    } else if (isUpKey) {
      this.historyCmdIndex = Math.max(this.historyCmdIndex - 1, 0)
    }
    if (isUpKey || isDownKey) {
      const historyCmd = this.historyCmdList[this.historyCmdIndex]
      if (historyCmd) {
        this.setState({
          command: historyCmd,
          cursorPosition: historyCmd.length
        })
        setTimeout(() => {
          setCaretPosition(this.$inputEl.current, historyCmd.length + 1)
        }, 0)
      }
    }

    if (isTabKey) {
      if (!command) {
        this.setState({ command: 'help', cursorPosition: 4 })
        e.preventDefault()
        return
      }

      const canExtendCmdList = this.allCmdList
        .filter((candidate) => candidate.startsWith(command.toLowerCase()))
        .sort()

      if (canExtendCmdList && canExtendCmdList.length) {
        const nextCommand = canExtendCmdList[0]
        this.setState({
          command: nextCommand,
          cursorPosition: nextCommand.length,
        })
      }
      e.preventDefault()
    }

    if (isCtrlCKey) {
      this.print(`${prompt}${command}`)
      this.setState({ command: '' })
      e.preventDefault()
    }

    if (isCtrlDKey) {
      this.print(tipCmdList.jump)
      e.preventDefault()
      window.history.go(-1)
    }

    if (isCtrlLKey) {
      this.setState({ cmdList: [] })
      e.preventDefault()
    }

    this.autoScroll()
    this.inputFocus()
  };

  updatePrompt = (directory) => {
    const {
      config: { prompt },
    } = this.props

    this.setState({
      currentPrompt: prompt || `➜  ${directory} `,
      directory
    })
  }

  resolveAppCommandEntry = (input) => {
    const { cmd } = this.props

    return (
      resolveCommandEntry(cmd.staticList, this.staticCommandAliasMap, input) ||
      resolveCommandEntry(cmd.dynamicList, this.dynamicCommandAliasMap, input) ||
      resolveSystemCommandEntry(input)
    )
  }

  getFeaturedCommandList = () => {
    const {
      config: { featuredCommands = [] },
    } = this.props

    return featuredCommands
      .map((commandName) => {
        const commandEntry = this.resolveAppCommandEntry(commandName)
        if (!commandEntry) {
          return null
        }

        return {
          type: 'success',
          label: commandEntry.key,
          content: formatCommandDescription(commandEntry.command),
        }
      })
      .filter(Boolean)
  }

  getSuggestedCommand = (input) => {
    if (!input) {
      return null
    }

    const rankedCommands = this.allCmdList
      .map((candidate) => ({
        candidate,
        score: getEditDistance(input, candidate),
      }))
      .sort((left, right) => {
        if (left.score !== right.score) {
          return left.score - right.score
        }

        return left.candidate.localeCompare(right.candidate)
      })

    const bestMatch = rankedCommands[0]
    if (!bestMatch || bestMatch.score > 3) {
      return null
    }

    const commandEntry = this.resolveAppCommandEntry(bestMatch.candidate)
    return commandEntry ? commandEntry.key : bestMatch.candidate
  }

  handleCommand = (e) => {
    const {
      cmd,
      config: { version: versionNumber },
    } = this.props
    const isEnterKey = e.keyCode === 13

    if (!isEnterKey) {
      this.handleKeyCommand(e)
      return
    }

    if (!this.state.command) {
      this.print(this.state.currentPrompt)
      return
    }

    const command = this.state.command.toLowerCase().trim()
    const [action, ...args] = command.split(' ')
    const commandArgs = args.join(' ')

    this.historyCmdList.push(command)
    this.historyCmdIndex = this.historyCmdList.length

    this.print(`${this.state.currentPrompt}${command}`)
    const cmdList = []

    const staticCommandEntry = resolveCommandEntry(
      cmd.staticList,
      this.staticCommandAliasMap,
      command
    )
    const dynamicCommandEntry = resolveCommandEntry(
      cmd.dynamicList,
      this.dynamicCommandAliasMap,
      action
    )
    const isStaticCommand = !!staticCommandEntry
    const isDynamicCommand = !!dynamicCommandEntry

    const {
      exit,
      help,
      clear,
      pwd,
      cd,
      version,
      date,
      whoami,
      history,
      env,
      echo,
    } = systemCmdList
    const { unknown, jump, supporting } = tipCmdList

    if (exit.aliasList.includes(action)) {
      cmdList.push(jump)
      this.print(cmdList)
      window.history.go(-1)
    } else if (help.aliasList.includes(action)) {
      if (commandArgs) {
        const commandEntry = this.resolveAppCommandEntry(commandArgs)

        if (commandEntry) {
          cmdList.push({
            type: 'info',
            label: commandEntry.key,
            content: formatCommandDescription(commandEntry.command),
          })
        } else {
          cmdList.push({
            type: 'error',
            label: 'Error',
            content: `No manual entry for ${commandArgs}`,
          })
        }
        this.print(cmdList)
      } else {
        const featuredCommands = this.getFeaturedCommandList()
        const portfolioCommands = this.supportedCmdList.map((commandKey) => {
          const command =
            cmd.staticList[commandKey] || cmd.dynamicList[commandKey]
          return {
            type: 'success',
            label: commandKey,
            content: formatCommandDescription(command),
          }
        })
        const systemCommands = Object.entries(systemCmdList).map(([key, command]) => ({
          type: 'system',
          label: key,
          content: formatCommandDescription(command),
        }))

        cmdList.push(supporting)
        if (featuredCommands.length) {
          cmdList.push({
            type: 'system',
            label: 'Start',
            content: 'Recommended commands for first-time visitors.',
          })
          cmdList.push(...featuredCommands)
        }
        cmdList.push({
          type: 'system',
          label: 'Portfolio',
          content: 'Main commands for exploring Dagm\'s work and background.',
        })
        cmdList.push(...portfolioCommands)
        cmdList.push({
          type: 'system',
          label: 'System',
          content: 'Built-in terminal commands.',
        })
        cmdList.push(...systemCommands)
        this.print(cmdList)
      }
    } else if (clear.aliasList.includes(action)) {
      this.setState({ cmdList: [] })
    } else if (pwd.aliasList.includes(action)) {
      this.print(this.state.directory)
    } else if (cd.aliasList.includes(action)) {
      if (commandArgs) {
        const directory = commandArgs.trim()
        if (directory && directory.length < 20) {
          this.updatePrompt(directory)
        }
      }
    } else if (version.aliasList.includes(action)) {
      this.print(versionNumber)
    } else if (date.aliasList.includes(action)) {
      this.print(new Date().toString())
    } else if (whoami.aliasList.includes(action)) {
      this.print('dagm')
    } else if (history.aliasList.includes(action)) {
      this.historyCmdList.forEach((cmd, index) => {
        cmdList.push(`${index + 1}  ${cmd}`)
      })
      this.print(cmdList)
    } else if (env.aliasList.includes(action)) {
      cmdList.push(
        'TERM=react-terminal',
        'SHELL=/bin/bash',
        'USER=dagm',
        'PATH=/usr/local/bin:/usr/bin:/bin',
        `PWD=${this.state.directory}`,
        'HOME=/home/dagm'
      )
      this.print(cmdList)
    } else if (echo.aliasList.includes(action)) {
      const text = commandArgs.replace(
        /\$([A-Za-z_][A-Za-z0-9_]*)/g,
        (match, varName) => {
          switch (varName) {
            case 'USER':
              return 'dagm'
            case 'PWD':
              return this.state.directory
            case 'HOME':
              return '/home/dagm'
            case 'SHELL':
              return '/bin/bash'
            case 'TERM':
              return 'react-terminal'
            case 'PATH':
              return '/usr/local/bin:/usr/bin:/bin'
            default:
              return match
          }
        }
      )
      this.print(text || '')
    } else if (isStaticCommand) {
      this.print(staticCommandEntry.command.list)
    } else if (isDynamicCommand) {
      this.run(dynamicCommandEntry.key, commandArgs)
    } else if (action.trim()) {
      const suggestedCommand = this.getSuggestedCommand(action)
      const unknownCommand = {
        ...unknown,
        content: suggestedCommand
          ? `${unknown.contentWithCommand(action)}. Did you mean \`${suggestedCommand}\`?`
          : unknown.contentWithCommand(action),
      }
      const guideMessage = {
        type: 'system',
        label: 'Guide',
        content: 'Type `guide` if you are new here, or `help` to see all commands.',
      }

      this.print([unknownCommand, guideMessage, help])
    }

    this.setState({ command: '' })
    setTimeout(this.autoScroll, 0)
    this.inputFocus()
  };

  render() {
    const { className } = this.props
    const { cmdList, isPrinting, command, directory, currentPrompt, cursorPosition } = this.state
    return (
      <StyledTerminalWrapper className={className}>
        <StyledHeader>
          <StyledHeaderTitle>{directory}</StyledHeaderTitle>
          <StyledHeaderDotList>
            <StyledHeaderDotItem color="red" />
            <StyledHeaderDotItem color="yellow" />
            <StyledHeaderDotItem color="green" />
          </StyledHeaderDotList>
        </StyledHeader>
        <StyledTerminal ref={this.$terminal} onClick={this.inputFocus}>
          <StyledTerminalInner>
            <TransitionGroup>
              {cmdList.map((item, index) => (
                <CSSTransition key={index} timeout={500}>
                  <StyledLine className={typeof item === 'string' ? '' : item.type || ''}>
                    {typeof item === 'string' ? (
                      <StyledCommand className="cmd">{item}</StyledCommand>
                    ) : (
                      <>
                        {item.time && (
                          <StyledCommand className="time">
                            {item.time}
                          </StyledCommand>
                        )}
                        {item.label && (
                          <StyledCommand className={item.type}>
                            {item.label}
                          </StyledCommand>
                        )}
                        {item.content && (
                          <StyledCommand className="cmd">
                            {item.content}
                          </StyledCommand>
                        )}
                      </>
                    )}
                  </StyledLine>
                </CSSTransition>
              ))}
            </TransitionGroup>
            <StyledInputWrapper
              ref={this.$inputWrapper}
              onClick={this.inputFocus}
            >
              {isPrinting ? (
                <StyledLoadingCursor>.</StyledLoadingCursor>
              ) : (
                <>
                  <StyledPrompt>{currentPrompt}</StyledPrompt>
                  <StyledCommand>{command.slice(0, cursorPosition)}</StyledCommand>
                  <StyledBlinkCursor>
                    {cursorPosition === command.length ? '\u00A0' : command[cursorPosition] || '\u00A0'}
                  </StyledBlinkCursor>
                  <StyledCommand>{command.slice(cursorPosition + (cursorPosition === command.length ? 0 : 1))}</StyledCommand>
                </>
              )}
              <StyledInput
                value={command}
                onChange={(e) => {
                  const newPos = e.target.selectionStart
                  this.setState({
                    command: e.target.value,
                    cursorPosition: newPos
                  })
                }}
                onKeyDown={this.handleCommand}
                onSelect={(e) => {
                  this.setState({
                    cursorPosition: e.target.selectionStart
                  })
                }}
                onFocus={(e) => {
                  this.setState({
                    cursorPosition: e.target.selectionStart
                  })
                }}
                autoFocus
                ref={this.$inputEl}
              />
            </StyledInputWrapper>
          </StyledTerminalInner>
        </StyledTerminal>
      </StyledTerminalWrapper>
    )
  }
}

export default Terminal
